import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { confirmDelete } from 'components/v2/confirm-dialog/confirmDelete';
import { StyledEditDialog } from 'components/v2/dialogs/ConfirmWithCommentDialog';
import { toaster } from 'components/v2/toast';
import { DataTableStyled } from 'components/v2/compiler/grids/StyledGrid';
import { withGreyBackground } from 'pages/v2/WithGreyBackground';
import { Container } from 'styles/Styles.styled';
import { H1NewUI, FormPanel } from 'styles/v2/Styles.styled';
import styled from 'styled-components';
import {
  FeatureFlagAdminModel,
  createAdminFeatureFlag,
  deleteAdminFeatureFlag,
  getAdminFeatureFlags,
  updateAdminFeatureFlag,
} from 'services/api/adminFeatureFlagsApi';
import { QUERY_KEYS } from 'services/api/constants';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0;
`;

const GridPanel = styled(FormPanel)`
  display: block;
  padding: 0;
  overflow: hidden;
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 12px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    font-weight: 600;
    font-size: 13px;
  }
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const emptyForm = () => ({ key: '', isEnabled: false });

const AdminFeatureFlags: React.FunctionComponent = () => {
  const queryClient = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const [newFlag, setNewFlag] = useState(emptyForm);

  const flagsQuery = useQuery({
    queryKey: [QUERY_KEYS.ADMIN_FEATURE_FLAGS],
    queryFn: () => getAdminFeatureFlags(),
    refetchOnWindowFocus: false,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ADMIN_FEATURE_FLAGS] });

  const toggleMutation = useMutation({
    mutationFn: ({ key, isEnabled }: { key: string; isEnabled: boolean }) => updateAdminFeatureFlag(key, isEnabled),
    onSuccess: () => {
      invalidate();
      toaster({ severity: 'success', summary: 'Success', details: 'Feature flag updated' });
    },
    onError: () => toaster({ severity: 'error', summary: 'Error', details: 'Failed to update feature flag' }),
  });

  const createMutation = useMutation({
    mutationFn: ({ key, isEnabled }: { key: string; isEnabled: boolean }) => createAdminFeatureFlag(key, isEnabled),
    onSuccess: () => {
      invalidate();
      setShowDialog(false);
      toaster({ severity: 'success', summary: 'Success', details: 'Feature flag created' });
    },
    onError: () =>
      toaster({ severity: 'error', summary: 'Error', details: 'Failed to create feature flag. Key may already exist.' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => deleteAdminFeatureFlag(key),
    onSuccess: () => {
      invalidate();
      toaster({ severity: 'success', summary: 'Success', details: 'Feature flag deleted' });
    },
    onError: () => toaster({ severity: 'error', summary: 'Error', details: 'Failed to delete feature flag' }),
  });

  const openAddDialog = () => {
    setNewFlag(emptyForm());
    setShowDialog(true);
  };

  const handleCreate = () => {
    if (!newFlag.key.trim()) {
      toaster({ severity: 'error', summary: 'Validation', details: 'Key is required' });
      return;
    }
    createMutation.mutate(newFlag);
  };

  const handleDelete = (row: FeatureFlagAdminModel) => {
    confirmDelete({
      caption: `Delete feature flag "${row.key}"?`,
      description: 'This will permanently remove the flag.',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      onConfirm: () => deleteMutation.mutate(row.key),
    });
  };

  const enabledBodyTemplate = (rowData: FeatureFlagAdminModel) => (
    <InputSwitch
      checked={rowData.isEnabled}
      disabled={toggleMutation.isPending}
      onChange={(e) => toggleMutation.mutate({ key: rowData.key, isEnabled: e.value ?? false })}
    />
  );

  const actionsBodyTemplate = (rowData: FeatureFlagAdminModel) => (
    <Button
      icon="pi pi-trash"
      className="p-button-rounded p-button-text p-button-danger"
      onClick={() => handleDelete(rowData)}
    />
  );

  const dateBodyTemplate = (value: string) => {
    if (!value) return '';
    return new Date(value).toLocaleString();
  };

  if (flagsQuery.isLoading) {
    return (
      <Container>
        <ProgressSpinner />
      </Container>
    );
  }

  return (
    <Container>
      <ConfirmDialog />
      <PageHeader>
        <H1NewUI>Feature Flags</H1NewUI>
        <Button label="Add flag" icon="pi pi-plus" className="p-button-sm" onClick={openAddDialog} />
      </PageHeader>

      <GridPanel>
        <DataTableStyled value={flagsQuery.data || []} sortField="key" sortOrder={1} emptyMessage="No feature flags found">
          <Column field="key" header="Key" sortable style={{ minWidth: '20rem' }} />
          <Column field="isEnabled" header="Enabled" body={enabledBodyTemplate} sortable style={{ minWidth: '7rem' }} />
          <Column field="modifiedBy" header="Modified By" sortable style={{ minWidth: '12rem' }} />
          <Column
            field="modifiedAt"
            header="Modified At"
            body={(row) => dateBodyTemplate(row.modifiedAt)}
            sortable
            style={{ minWidth: '12rem' }}
          />
          <Column body={actionsBodyTemplate} style={{ width: '5rem' }} />
        </DataTableStyled>
      </GridPanel>

      <StyledEditDialog
        visible={showDialog}
        isOpened={showDialog}
        header="Add Feature Flag"
        style={{ width: '420px' }}
        onHide={() => setShowDialog(false)}
        footer={
          <div>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setShowDialog(false)} />
            <Button label="Save" icon="pi pi-check" onClick={handleCreate} loading={createMutation.isPending} />
          </div>
        }
      >
        <FormGrid>
          <FormField>
            <label htmlFor="flagKey">Key *</label>
            <InputText
              id="flagKey"
              value={newFlag.key}
              onChange={(e) => setNewFlag({ ...newFlag, key: e.target.value })}
              placeholder="e.g. my-feature.enabled"
            />
          </FormField>
          <SwitchRow>
            <InputSwitch
              id="flagEnabled"
              checked={newFlag.isEnabled}
              onChange={(e) => setNewFlag({ ...newFlag, isEnabled: e.value ?? false })}
            />
            <label htmlFor="flagEnabled">Enabled</label>
          </SwitchRow>
        </FormGrid>
      </StyledEditDialog>
    </Container>
  );
};

export default withGreyBackground(AdminFeatureFlags);
